import React, { useState, useEffect } from 'react';
import { 
  relationshipTypesApi, 
  RelationshipType, 
  RelationshipTypeCreate 
} from '../api/client';
import { getErrorMessage } from '../utils/errorHandler';
import { PlusIcon, PencilIcon, TrashIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

const RelationshipTypesManager: React.FC = () => {
  const [relationshipTypes, setRelationshipTypes] = useState<RelationshipType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Add new type form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newType, setNewType] = useState<RelationshipTypeCreate>({
    name: '',
    display_name: ''
  });
  const [addingType, setAddingType] = useState(false);
  
  // Edit type
  const [editingTypeId, setEditingTypeId] = useState<number | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');

  useEffect(() => {
    loadRelationshipTypes();
  }, []);

  const loadRelationshipTypes = async () => {
    try {
      setLoading(true);
      const types = await relationshipTypesApi.getRelationshipTypes();
      setRelationshipTypes(types);
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleAddType = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newType.name || !newType.display_name) {
      setError('Both name and display name are required');
      return;
    }
    
    // Validate name format (lowercase, underscores only)
    if (!/^[a-z_]+$/.test(newType.name)) {
      setError('Name must be lowercase letters and underscores only (e.g., vip_guest)');
      return;
    }
    
    setAddingType(true);
    setError(null);
    
    try {
      const createdType = await relationshipTypesApi.createRelationshipType(newType);
      setRelationshipTypes([...relationshipTypes, createdType]);
      setNewType({ name: '', display_name: '' });
      setShowAddForm(false);
      setSuccess('Relationship type added successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setAddingType(false);
    }
  };

  const handleStartEdit = (type: RelationshipType) => {
    setEditingTypeId(type.id);
    setEditDisplayName(type.display_name);
    setError(null);
  };

  const handleSaveEdit = async (typeId: number) => {
    if (!editDisplayName) {
      setError('Display name cannot be empty');
      return;
    }
    
    try {
      const updatedType = await relationshipTypesApi.updateRelationshipType(typeId, {
        display_name: editDisplayName
      });
      
      setRelationshipTypes(relationshipTypes.map(t => 
        t.id === typeId ? updatedType : t
      ));
      
      setEditingTypeId(null);
      setSuccess('Relationship type updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(getErrorMessage(error));
    }
  };

  const handleCancelEdit = () => {
    setEditingTypeId(null);
    setEditDisplayName('');
    setError(null);
  };

  const handleToggleActive = async (type: RelationshipType) => {
    try {
      const updatedType = await relationshipTypesApi.updateRelationshipType(type.id, {
        is_active: !type.is_active
      });
      
      setRelationshipTypes(relationshipTypes.map(t => 
        t.id === type.id ? updatedType : t
      ));
      
      setSuccess(`Relationship type ${updatedType.is_active ? 'activated' : 'deactivated'} successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(getErrorMessage(error));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Relationship Types</h2>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
          >
            <PlusIcon className="h-4 w-4" />
            Add Type
          </button>
        )}
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 text-sm">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 rounded p-3 text-green-700 text-sm">
          {success}
        </div>
      )}

      {/* Add New Type Form */}
      {showAddForm && (
        <form onSubmit={handleAddType} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium mb-3">Add New Relationship Type</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Internal Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newType.name}
                onChange={(e) => setNewType({ ...newType, name: e.target.value.toLowerCase().replace(/[^a-z_]/g, '_') })}
                placeholder="e.g., vip_guest"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="mt-1 text-xs text-gray-500">Lowercase letters and underscores only</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Display Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newType.display_name}
                onChange={(e) => setNewType({ ...newType, display_name: e.target.value })}
                placeholder="e.g., VIP Guest"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="mt-1 text-xs text-gray-500">Shown to users in forms</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setNewType({ name: '', display_name: '' });
                setError(null);
              }}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addingType}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
            >
              {addingType ? 'Adding...' : 'Add Type'}
            </button>
          </div>
        </form>
      )}

      {/* Relationship Types List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Display Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Internal Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {relationshipTypes.map((type) => (
              <tr key={type.id} className={!type.is_active ? 'bg-gray-50' : ''}>
                <td className="px-4 py-3 whitespace-nowrap">
                  {editingTypeId === type.id ? (
                    <input
                      type="text"
                      value={editDisplayName}
                      onChange={(e) => setEditDisplayName(e.target.value)}
                      className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  ) : (
                    <span className={`text-sm ${!type.is_active ? 'text-gray-400' : 'text-gray-900'}`}>
                      {type.display_name}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`text-sm font-mono ${!type.is_active ? 'text-gray-400' : 'text-gray-600'}`}>
                    {type.name}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    type.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {type.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                  {editingTypeId === type.id ? (
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => handleSaveEdit(type.id)}
                        className="p-1 text-green-600 hover:text-green-900"
                        title="Save"
                      >
                        <CheckIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="p-1 text-red-600 hover:text-red-900"
                        title="Cancel"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => handleStartEdit(type)}
                        className="p-1 text-blue-600 hover:text-blue-900"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(type)}
                        className={`p-1 ${
                          type.is_active 
                            ? 'text-red-600 hover:text-red-900' 
                            : 'text-green-600 hover:text-green-900'
                        }`}
                        title={type.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {type.is_active ? (
                          <TrashIcon className="h-4 w-4" />
                        ) : (
                          <CheckIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {relationshipTypes.length === 0 && (
          <div className="px-4 py-8 text-center text-gray-500 text-sm">
            No relationship types found. Add your first type above.
          </div>
        )}
      </div>
      
      <p className="text-xs text-gray-500">
        Note: Deactivated types won't appear in forms but existing data will be preserved.
      </p>
    </div>
  );
};

export default RelationshipTypesManager;